/**
 * Pure FLIP delta calculator.
 *
 * Given a "First" snapshot (`prev`) captured before a reorder and a
 * "Last" snapshot (`next`) captured after React has re-rendered the new
 * list order, returns the `{ dx, dy }` pixel delta each item needs to
 * be visually inverted by before the "Play" phase animates it back to
 * zero.
 *
 * This function is intentionally DOM-free and React-free so it can be
 * unit-tested in isolation. The hook that calls it owns all
 * `getBoundingClientRect()` reads and all `style.transform` writes —
 * `computeFlipDeltas` only does rect arithmetic.
 *
 * Rules:
 * - Only keys present in both `prev` and `next` produce an entry.
 * - Only items whose rect actually changed produce an entry (zero-delta
 *   entries are omitted so callers can skip a no-op write).
 */
export const computeFlipDeltas = (
  prev: Map<string, DOMRect>,
  next: Map<string, DOMRect>
): Map<string, { dx: number; dy: number }> => {
  const result = new Map<string, { dx: number; dy: number }>()

  for (const [key, prevRect] of prev) {
    const nextRect = next.get(key)
    if (!nextRect) {
      continue
    }
    const dx = prevRect.left - nextRect.left
    const dy = prevRect.top - nextRect.top
    if (dx === 0 && dy === 0) {
      continue
    }
    result.set(key, { dx, dy })
  }

  return result
}
